import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Edit, Trash2, Key, Plus, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, X } from 'lucide-react';
import api from '../../utils/api';

interface Agent {
  id: number;
  first_name: string;
  last_name: string;
  email: string | null;
  password?: string;
  created_at?: string;
  updated_at?: string;
}

type SortOrder = 'asc' | 'desc' | null;

interface LazyParams {
  first: number;
  rows: number;
  page: number;
  sortField: string | null;
  sortOrder: SortOrder;
  filters: Record<string, any>;
}

export default function Agents() {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [resetPasswordDialogVisible, setResetPasswordDialogVisible] = useState(false);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [lazyParams, setLazyParams] = useState<LazyParams>({
    first: 0,
    rows: 15,
    page: 1,
    sortField: null,
    sortOrder: null,
    filters: {}
  });
  const { t } = useTranslation();
  const [toast, setToast] = useState({ visible: false, severity: '', message: '' });

  useEffect(() => {
    fetchAgents();
  }, [lazyParams]);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const response = await api.get('/agents', {
        params: {
          page: lazyParams.page,
          per_page: lazyParams.rows,
          sort_field: lazyParams.sortField || undefined,
          sort_order: lazyParams.sortOrder || undefined,
          ...lazyParams.filters
        }
      });
      setAgents(response.data.data);
      setTotalRecords(response.data.total);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching agents:', error);
      showToast('error', t('error_fetching_agents'));
      setLoading(false);
    }
  };

  const showToast = (severity: string, message: string) => {
    setToast({ visible: true, severity, message });
    setTimeout(() => setToast({ ...toast, visible: false }), 3000);
  };

  const openNew = () => {
    setEditingAgent({
      id: 0,
      first_name: '',
      last_name: '',
      email: '',
      password: '',
    });
    setDialogVisible(true);
  };

  const hideDialog = () => {
    setDialogVisible(false);
    setEditingAgent(null);
  };

  const hideResetPasswordDialog = () => {
    setResetPasswordDialogVisible(false);
    setNewPassword('');
  };

  const saveAgent = async () => {
    if (!editingAgent) return;

    try {
      if (editingAgent.id === 0) {
        await api.post('/agents', editingAgent);
        showToast('success', t('agent_created'));
      } else {
        const { password, ...agentWithoutPassword } = editingAgent;
        await api.put(`/agents/${editingAgent.id}`, agentWithoutPassword);
        showToast('success', t('agent_updated'));
      }
      fetchAgents();
      hideDialog();
    } catch (error) {
      console.error('Error saving agent:', error);
      showToast('error', t('error_saving_agent'));
    }
  };

  const editAgent = (agent: Agent) => {
    setEditingAgent({ ...agent, password: '' });
    setDialogVisible(true);
  };

  const confirmDeleteAgent = (agent: Agent) => {
    if (window.confirm(t('confirm_delete_agent'))) {
      deleteAgent(agent);
    }
  };

  const deleteAgent = async (agent: Agent) => {
    try {
      await api.delete(`/agents/${agent.id}`);
      showToast('success', t('agent_deleted'));
      fetchAgents();
    } catch (error) {
      console.error('Error deleting agent:', error);
      showToast('error', t('error_deleting_agent'));
    }
  };

  const openResetPasswordDialog = (agent: Agent) => {
    setEditingAgent(agent);
    setResetPasswordDialogVisible(true);
  };

  const resetPassword = async () => {
    if (!editingAgent || !newPassword) return;

    try {
      await api.post(`/agents/${editingAgent.id}/reset-password`, { new_password: newPassword });
      showToast('success', t('password_reset_success'));
      hideResetPasswordDialog();
    } catch (error) {
      console.error('Error resetting password:', error);
      showToast('error', t('error_resetting_password'));
    }
  };

  const onPage = (page: number) => {
    setLazyParams(prevState => ({ ...prevState, page, first: page * lazyParams.rows }));
  };

  const onSort = (field: string) => {
    const sortOrder = lazyParams.sortField === field && lazyParams.sortOrder === 'asc' ? 'desc' : 'asc';
    setLazyParams(prevState => ({ ...prevState, sortField: field, sortOrder }));
  };

  const onFilter = (field: string, value: string) => {
    setLazyParams(prevState => ({
      ...prevState,
      first: 0,
      page: 1,
      filters: { ...prevState.filters, [field]: value }
    }));
  };

  const formatDate = (value: string) => {
    return new Date(value).toLocaleString();
  };

  return (
    <div className="p-6 min-h-screen bg-gray-100">
      {toast.visible && (
        <div className={`fixed top-4 right-4 p-4 rounded-md shadow-md ${
          toast.severity === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white`}>
          {toast.message}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800">{t('manage_agents')}</h2>
          <button
            onClick={openNew}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition duration-200"
          >
            <Plus className="inline-block mr-2" size={18} />
            {t('new_agent')}
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => onSort('first_name')}
                >
                  {t('first_name')}
                  {lazyParams.sortField === 'first_name' && (
                    <span className="ml-2">
                      {lazyParams.sortOrder === 'asc' ? '▲' : '▼'}
                    </span>
                  )}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => onSort('last_name')}
                >
                  {t('last_name')}
                  {lazyParams.sortField === 'last_name' && (
                    <span className="ml-2">
                      {lazyParams.sortOrder === 'asc' ? '▲' : '▼'}
                    </span>
                  )}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('email')}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => onSort('created_at')}
                >
                  {t('created_at')}
                  {lazyParams.sortField === 'created_at' && (
                    <span className="ml-2">
                      {lazyParams.sortOrder === 'asc' ? '▲' : '▼'}
                    </span>
                  )}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => onSort('updated_at')}
                >
                  {t('updated_at')}
                  {lazyParams.sortField === 'updated_at' && (
                    <span className="ml-2">
                      {lazyParams.sortOrder === 'asc' ? '▲' : '▼'}
                    </span>
                  )}
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center">{t('loading')}</td>
                </tr>
              ) : agents.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center">{t('no_agents_found')}</td>
                </tr>
              ) : (
                agents.map((agent) => (
                  <tr key={agent.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">{agent.first_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{agent.last_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{agent.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{formatDate(agent.created_at || '')}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{formatDate(agent.updated_at || '')}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => editAgent(agent)}
                        className="text-indigo-600 hover:text-indigo-900 mr-2"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => openResetPasswordDialog(agent)}
                        className="text-yellow-600 hover:text-yellow-900 mr-2"
                      >
                        <Key size={18} />
                      </button>
                      <button
                        onClick={() => confirmDeleteAgent(agent)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
          <div>
            <p className="text-sm text-gray-700">
              {t('showing')} <span className="font-medium">{lazyParams.first + 1}</span> {t('to')} <span className="font-medium">{Math.min(lazyParams.first + lazyParams.rows, totalRecords)}</span> {t('of')}{' '}
              <span className="font-medium">{totalRecords}</span> {t('results')}
            </p>
          </div>
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              <button
                onClick={() => onPage(1)}
                disabled={lazyParams.page === 1}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
              >
                <span className="sr-only">{t('first')}</span>
                <ChevronsLeft className="h-5 w-5" aria-hidden="true" />
              </button>
              <button
                onClick={() => onPage(lazyParams.page - 1)}
                disabled={lazyParams.page === 1}
                className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
              >
                <span className="sr-only">{t('previous')}</span>
                <ChevronLeft className="h-5 w-5" aria-hidden="true" />
              </button>
              <span className="relative
 inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                {t('page')} {lazyParams.page} {t('of')} {Math.ceil(totalRecords / lazyParams.rows)}
              </span>
              <button
                onClick={() => onPage(lazyParams.page + 1)}
                disabled={lazyParams.page === Math.ceil(totalRecords / lazyParams.rows)}
                className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
              >
                <span className="sr-only">{t('next')}</span>
                <ChevronRight className="h-5 w-5" aria-hidden="true" />
              </button>
              <button
                onClick={() => onPage(Math.ceil(totalRecords / lazyParams.rows))}
                disabled={lazyParams.page === Math.ceil(totalRecords / lazyParams.rows)}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
              >
                <span className="sr-only">{t('last')}</span>
                <ChevronsRight className="h-5 w-5" aria-hidden="true" />
              </button>
            </nav>
          </div>
        </div>
      </div>

      {dialogVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold">{editingAgent && editingAgent.id ? t('edit_agent') : t('new_agent')}</h2>
              <button onClick={hideDialog} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); saveAgent(); }} className="p-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">{t('first_name')}</label>
                  <input
                    type="text"
                    id="first_name"
                    value={editingAgent?.first_name || ''}
                    onChange={(e) => setEditingAgent(prev => prev ? { ...prev, first_name: e.target.value } : null)}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">{t('last_name')}</label>
                  <input
                    type="text"
                    id="last_name"
                    value={editingAgent?.last_name || ''}
                    onChange={(e) => setEditingAgent(prev => prev ? { ...prev, last_name: e.target.value } : null)}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">{t('email')}</label>
                  <input
                    type="tel"
                    id="email"
                    value={editingAgent?.email || ''}
                    onChange={(e) => setEditingAgent(prev => prev ? { ...prev, email: e.target.value } : null)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                {editingAgent?.id === 0 && (
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">{t('password')}</label>
                    <input
                      type="password"
                      id="password"
                      value={editingAgent?.password || ''}
                      onChange={(e) => setEditingAgent(prev => prev ? { ...prev, password: e.target.value } : null)}
                      required
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                )}
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={hideDialog}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {t('save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {resetPasswordDialogVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold">{t('reset_password')}</h2>
              <button onClick={hideResetPasswordDialog} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); resetPassword(); }} className="p-6">
              <div>
                <label htmlFor="new_password" className="block text-sm font-medium text-gray-700">{t('new_password')}</label>
                <input
                  type="password"
                  id="new_password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={hideResetPasswordDialog}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {t('reset')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

